/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { addErrorMessage } from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { ParsedOpenAPIExtension } from '@ibm-watson/shared/ts/extensions';
import { IntegrationSchema } from '@ibm-watson/shared/ts/integration';
import { CreateAsyncThunk } from '@ibm-watson/shared/ts/state';
import { transformOpenAPISpec } from '../../util/openAPISpecParser';
import { selectCatalogItemById } from '../catalog/selectors';

/**
 * Creates a parsed instance of the added open api custom extension integration.
 * @param {object} OpenAPIExtensionInstance Catalog openapi information to create
 */
export const parseOpenAPIExtension = createAsyncThunk<
  ParsedOpenAPIExtension,
  IntegrationSchema,
  CreateAsyncThunk
>(
  'parsedOpenAPIExtensions/upsertParsedOpenAPIExtension',
  async (integration, { dispatch, getState, rejectWithValue }) => {
    const catalogItem = selectCatalogItemById(
      getState(),
      integration.settings?.catalog_item_id
    );

    // If it is not there, it is a duplicate instance (non-draft) and we can ignore.
    if (_.isNil(catalogItem)) {
      return undefined;
    }

    try {
      // Transform the API spec freshly. This takes the OpenAPI JSON and transforms it into a format that is friendly
      // for actions. We want to do this on the fly and merge with what is saved in config so we have more freedom to
      // change or add items to this parsing as the feature grows in maturity.
      const transformedSpec = await transformOpenAPISpec(
        catalogItem.specification.body
      );

      return {
        ..._.cloneDeep(integration),
        settings: {
          ..._.cloneDeep(integration.settings),
          spec_hash_id: catalogItem.spec_hash_id,
          // Using the newly parsed version of the endpoints as a base, we iterate over it and merge the properties of
          // the version saved into configuration into it.
          endpoints: transformedSpec.endpoints
        }
      } as ParsedOpenAPIExtension;
    } catch (e) {
      dispatch(addErrorMessage(e));
      return rejectWithValue(e);
    }
  }
);
